import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, FileText } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, requestWithMessage, type Paginated } from '../lib/api';
import { addDays, formatDateTime, localIsoDate } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { usePeriod } from '../lib/period';
import { Button, Card, EmptyState, ErrorState, Field, Input, LoadingBlock, PageHeader, Pagination, Select, Table, Td, Th, Tr, useToast } from '../components/ui';
import { Section, SnapshotStatus, useNumber } from '../components/kit';
import { DayRange, hrefWith, lastDayOf } from '../components/a-common';
import { AiStatusBadge, CoverageSummary, useKindText, type ReportCoverage } from '../components/a-ai';

/**
 * Executive Reports (CR-008 §13, §17): the stored snapshots of days, weeks, months and ranges — never
 * overwritten: a change in their sources is a new revision, with why. Filtered by kind and dates, each
 * row opens the report. A snapshot of a period can be asked for here (dashboard metadata only — nothing
 * in the business changes).
 */

export const REPORT_KINDS = ['DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM'] as const;
type Kind = (typeof REPORT_KINDS)[number];

interface ReportRow {
  id: string;
  kind: string;
  periodStart: string;
  periodEnd: string;
  timezone: string;
  status: string;
  revision: number;
  generatedBy: string;
  computedAt: string;
  finalizedAt: string | null;
  coverage: ReportCoverage | null;
  definitionVersion: string;
  settingsVersion: number;
  ai: string;
}

export function ReportsPage() {
  const { t, locale, has } = useI18n();
  const { param, setParam, query } = usePeriod();
  const [, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const num = useNumber();
  const kindText = useKindText();
  // Own names in the address: `from`/`to` are the period picker's.
  const kind = param('kind');
  const since = param('since');
  const until = param('until');
  const page = Math.max(1, Number(param('page')) || 1);

  const list = useQuery({
    queryKey: ['executive', 'reports', { kind, since, until, page }],
    queryFn: () => api.get<Paginated<ReportRow>>('/executive/reports', { kind: kind || undefined, from: since || undefined, to: until || undefined, page }),
    placeholderData: keepPreviousData,
  });
  const filtered = !!(kind || since || until);
  const generatedBy = (g: string) => {
    const key = `reports.generatedBy.${g}`;
    return has(key) ? t(key) : g;
  };

  return (
    <>
      <PageHeader title={t('reports.title')} subtitle={t('reports.subtitle')} count={list.data?.total} />

      <div className="space-y-6">
        <SnapshotForm />

        <Card padded={false}>
          <div className="flex flex-wrap items-end gap-3 border-b hairline p-4">
            <Field label={t('reports.filter.kind')}>
              <Select value={kind} onChange={(e) => setParam('kind', e.target.value || null)} className="!w-auto min-w-[10rem]">
                <option value="">{t('common.all')}</option>
                {REPORT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {kindText(k)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('reports.filter.since')}>
              <Input type="date" dir="ltr" value={since} max={until || undefined} onChange={(e) => setParam('since', e.target.value || null)} className="!w-auto" />
            </Field>
            <Field label={t('reports.filter.until')}>
              <Input type="date" dir="ltr" value={until} min={since || undefined} onChange={(e) => setParam('until', e.target.value || null)} className="!w-auto" />
            </Field>
            {filtered && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  // One update: three would each start from the same address and only the last would stay.
                  setSearch(
                    (prev) => {
                      const out = new URLSearchParams(prev);
                      for (const k of ['kind', 'since', 'until', 'page']) out.delete(k);
                      return out;
                    },
                    { replace: true },
                  )
                }
              >
                {t('common.clear')}
              </Button>
            )}
          </div>

          {list.isLoading ? (
            <LoadingBlock />
          ) : list.error || !list.data ? (
            <ErrorState error={list.error ?? t('common.error')} onRetry={() => void list.refetch()} />
          ) : list.data.items.length === 0 ? (
            <EmptyState icon={<FileText className="h-6 w-6" aria-hidden />} title={filtered ? t('reports.noneFiltered') : t('reports.none')} hint={filtered ? t('common.noResultsHint') : t('reports.noneHint')} />
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('reports.col.period')}</Th>
                    <Th>{t('reports.col.kind')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th align="end">{t('reports.col.revision')}</Th>
                    <Th>{t('reports.col.generatedBy')}</Th>
                    <Th>{t('reports.col.computed')}</Th>
                    <Th>{t('reports.col.finalized')}</Th>
                    <Th>{t('reports.col.ai')}</Th>
                    <Th>{t('reports.col.coverage')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((r) => (
                    <Tr key={r.id} onClick={() => navigate(hrefWith(`/reports/${r.id}`, query))}>
                      <Td>
                        <Link to={hrefWith(`/reports/${r.id}`, query)} onClick={(e) => e.stopPropagation()} className="text-primary-600 hover:underline dark:text-primary-400">
                          <DayRange start={r.periodStart} last={lastDayOf(r.periodEnd)} />
                        </Link>
                      </Td>
                      <Td className="whitespace-nowrap">{kindText(r.kind)}</Td>
                      <Td>
                        <SnapshotStatus status={r.status} />
                      </Td>
                      <Td align="end" className="tabular">
                        {num(r.revision)}
                      </Td>
                      <Td className="whitespace-nowrap">{generatedBy(r.generatedBy)}</Td>
                      <Td className="whitespace-nowrap text-caption text-muted">{formatDateTime(r.computedAt, locale)}</Td>
                      <Td className="whitespace-nowrap text-caption text-muted">{r.finalizedAt ? formatDateTime(r.finalizedAt, locale) : t('reports.notFinalized')}</Td>
                      <Td>
                        <AiStatusBadge status={r.ai} />
                      </Td>
                      <Td>
                        <CoverageSummary coverage={r.coverage} />
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
              <Pagination page={list.data.page} pageCount={list.data.pageCount} total={list.data.total} onChange={(p) => setParam('page', String(p))} />
            </>
          )}
        </Card>
      </div>
    </>
  );
}

/** "Make a snapshot": a period stored as computed now. The same sources give the same report. */
function SnapshotForm() {
  const { t, has } = useI18n();
  const { query } = usePeriod();
  const toast = useToast();
  const queryClient = useQueryClient();
  const kindText = useKindText();
  const today = localIsoDate();
  const yesterday = addDays(today, -1);
  const lastMonth = addDays(`${today.slice(0, 7)}-01`, -1).slice(0, 7);
  const [kind, setKind] = useState<Kind>('DAILY');
  const [date, setDate] = useState(yesterday);
  const [month, setMonth] = useState(lastMonth);
  const [from, setFrom] = useState(`${today.slice(0, 7)}-01`);
  const [to, setTo] = useState(yesterday < `${today.slice(0, 7)}-01` ? today : yesterday);
  const [saved, setSaved] = useState<{ id: string; revision: number; status: string } | null>(null);

  const body = () =>
    kind === 'MONTHLY' ? { period: kind, month } : kind === 'CUSTOM' ? { period: kind, from, to } : { period: kind, date };
  const ready = kind === 'MONTHLY' ? !!month : kind === 'CUSTOM' ? !!from && !!to && from <= to : !!date;

  const snapshot = useMutation({
    mutationFn: () => requestWithMessage<{ id: string; revision: number; status: string; reused?: boolean }>('/executive/reports', { method: 'POST', body: body() }),
    onSuccess: ({ data }) => {
      const statusKey = `status.${data.status}`;
      // Saved first: a period already saved returns its saved report — nothing is computed again.
      toast.success(data.reused ? t('reports.snapshot.reused') : t('reports.snapshot.saved', { revision: data.revision, status: has(statusKey) ? t(statusKey) : data.status }));
      setSaved(data);
      void queryClient.invalidateQueries({ queryKey: ['executive', 'reports'] });
      void queryClient.invalidateQueries({ queryKey: ['executive', 'overview'] });
      void queryClient.invalidateQueries({ queryKey: ['executive', 'insights'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
  });

  return (
    <Section title={t('reports.snapshot.title')} subtitle={t('reports.snapshot.hint')}>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) snapshot.mutate();
        }}
      >
        <Field label={t('reports.snapshot.kind')}>
          <Select value={kind} onChange={(e) => setKind(e.target.value as Kind)} className="!w-auto min-w-[10rem]">
            {REPORT_KINDS.map((k) => (
              <option key={k} value={k}>
                {kindText(k)}
              </option>
            ))}
          </Select>
        </Field>
        {(kind === 'DAILY' || kind === 'WEEKLY') && (
          <Field label={kind === 'DAILY' ? t('reports.snapshot.day') : t('reports.snapshot.dayInWeek')}>
            <Input type="date" dir="ltr" required value={date} max={today} onChange={(e) => setDate(e.target.value)} className="!w-auto" />
          </Field>
        )}
        {kind === 'MONTHLY' && (
          <Field label={t('reports.snapshot.month')}>
            <Input type="month" dir="ltr" required value={month} max={today.slice(0, 7)} onChange={(e) => setMonth(e.target.value)} className="!w-auto" />
          </Field>
        )}
        {kind === 'CUSTOM' && (
          <>
            <Field label={t('common.from')}>
              <Input type="date" dir="ltr" required value={from} max={to || today} onChange={(e) => setFrom(e.target.value)} className="!w-auto" />
            </Field>
            <Field label={t('common.to')}>
              <Input type="date" dir="ltr" required value={to} min={from || undefined} max={today} onChange={(e) => setTo(e.target.value)} className="!w-auto" />
            </Field>
          </>
        )}
        <Button type="submit" variant="primary" icon={<Camera className="h-4 w-4" aria-hidden />} loading={snapshot.isPending} disabled={!ready}>
          {snapshot.isPending ? t('reports.snapshot.saving') : t('reports.snapshot.submit')}
        </Button>
      </form>
      <p className="mt-3 text-caption text-muted">{t('reports.snapshot.note')}</p>
      {saved && (
        <p className="mt-2 text-body-sm">
          <Link to={hrefWith(`/reports/${saved.id}`, query)} className="text-primary-600 hover:underline dark:text-primary-400">
            {t('reports.snapshot.open', { revision: saved.revision })}
          </Link>
        </p>
      )}
    </Section>
  );
}
